// =============================================================================
// features/dashboard/utils/metrics.ts — Lógica pura dos KPIs e da série diária.
//
// Extraída dos componentes por um motivo concreto: a suíte do web não tem
// testing-library, então lógica que mora dentro do JSX só pode ser testada por
// uma RÉPLICA no arquivo de teste — e réplica não acompanha refatoração. Foi
// exatamente o que aconteceu com o cálculo de conversão do StatsRow: o teste
// continuou verde testando uma cópia da fórmula errada.
//
// Aqui as funções são as MESMAS que os componentes chamam.
// =============================================================================

import type { ConversionMetrics, DailySeriesItem } from '../../../hooks/dashboard/types';

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

/** Número no locale pt-BR: 4077 → "4.077". */
export function fmtNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}

/** Percentual já em escala 0-100: 23.45 → "23,5%". */
export function fmtPercent(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
}

/** Média com uma casa: 12.25 → "12,3". */
export function fmtAvg(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

// ---------------------------------------------------------------------------
// Conversão
// ---------------------------------------------------------------------------

export interface ConversionDisplay {
  value: string;
  description: string;
}

/** Taxa de conversão em pontos percentuais, 2 casas: 0.67 → "0,67%". */
export function fmtConversionRate(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

/**
 * Texto do card de Conversão: concluídos ÷ leads novos no período (F52-S01).
 *
 * `rate === null` significa "sem leads novos no período" (base 0) — diferente de
 * 0%, que significa "houve leads novos e nenhum foi concluído". A taxa não é
 * cortada em 100: concluído hoje de lead criado antes do período pode passar
 * disso, e o número real é o que a operação precisa ver.
 */
export function conversionDisplay(
  conversion: ConversionMetrics,
  rangeLabel: string,
): ConversionDisplay {
  const period = rangeLabel.toLowerCase();

  if (conversion.rate === null) {
    return {
      value: '—',
      description:
        conversion.concluded > 0
          ? `${fmtNumber(conversion.concluded)} ${conversion.concluded === 1 ? 'concluído' : 'concluídos'}, sem leads novos em ${period}`
          : `sem leads novos em ${period}`,
    };
  }

  const concluded = `${fmtNumber(conversion.concluded)} ${conversion.concluded === 1 ? 'concluído' : 'concluídos'}`;
  const base = `${fmtNumber(conversion.base)} ${conversion.base === 1 ? 'lead novo' : 'leads novos'}`;
  return {
    value: fmtConversionRate(conversion.rate),
    description: `${concluded} de ${base} — ${period}`,
  };
}

// ---------------------------------------------------------------------------
// Série diária
// ---------------------------------------------------------------------------

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

/**
 * Dia da semana de uma data `YYYY-MM-DD` (0 = domingo).
 *
 * Interpretada como meia-noite UTC de propósito: a string já é o dia de
 * calendário LOCAL que o servidor resolveu. Passá-la por `new Date('2026-08-21')`
 * sem sufixo deixaria o fuso do navegador deslocar o dia — o mesmo erro de fuso
 * que este trabalho corrigiu no backend, só que do lado do cliente.
 */
export function weekdayOf(day: string): number {
  return new Date(`${day}T00:00:00Z`).getUTCDay();
}

/** `2026-08-21` → `21/08`. */
export function shortLabel(day: string): string {
  const [, month, date] = day.split('-');
  return `${date ?? ''}/${month ?? ''}`;
}

export interface SeriesBucket {
  key: string;
  label: string;
  /** Rótulo completo, usado no tooltip. */
  fullLabel: string;
  interactions: number;
  leads: number;
  /** Quantos dias de calendário compõem o balde (>1 só na visão semanal). */
  days: number;
}

/** Um balde por dia, na ordem em que o servidor mandou. */
export function toDailyBuckets(data: DailySeriesItem[]): SeriesBucket[] {
  return data.map((d) => ({
    key: d.day,
    label: shortLabel(d.day),
    fullLabel: `${WEEKDAY_LABELS[weekdayOf(d.day)] ?? ''} ${shortLabel(d.day)}`,
    interactions: d.interactions,
    leads: d.leads,
    days: 1,
  }));
}

/**
 * Agrega a série por dia da semana, de segunda a domingo.
 *
 * `days` conta quantas ocorrências daquele dia da semana caíram no período — é o
 * denominador da média. Sem ele, comparar "segunda" (que apareceu 5 vezes num
 * mês) com "domingo" (4 vezes) seria comparar coisas diferentes.
 */
export function toWeekdayBuckets(data: DailySeriesItem[]): SeriesBucket[] {
  const order = [1, 2, 3, 4, 5, 6, 0]; // segunda → domingo
  const totals = new Map<number, { interactions: number; leads: number; days: number }>();

  for (const d of data) {
    const wd = weekdayOf(d.day);
    const acc = totals.get(wd) ?? { interactions: 0, leads: 0, days: 0 };
    acc.interactions += d.interactions;
    acc.leads += d.leads;
    acc.days += 1;
    totals.set(wd, acc);
  }

  return order
    .filter((wd) => totals.has(wd))
    .map((wd) => {
      const acc = totals.get(wd) ?? { interactions: 0, leads: 0, days: 0 };
      const label = WEEKDAY_LABELS[wd] ?? '';
      return {
        key: String(wd),
        label,
        fullLabel: label,
        interactions: acc.interactions,
        leads: acc.leads,
        days: acc.days,
      };
    });
}

/** Média diária de um balde. 0 dias → 0 (não NaN). */
export function bucketAverage(bucket: SeriesBucket): number {
  return bucket.days > 0 ? bucket.interactions / bucket.days : 0;
}
